<h2 mat-dialog-title>Bulk Import Purchase Orders</h2>

<mat-dialog-content class="bulk-import-content">

    <!-- ── IDLE: upload zone ── -->
    <ng-container *ngIf="state === 'idle'">
        <p class="hint-text">
            Upload a <strong>.csv</strong>, <strong>.xlsx</strong> or <strong>.xls</strong> file containing multiple purchase orders.
            The file must include a header row.
        </p>

        <div class="drop-zone"
             (dragover)="onDragOver($event)"
             (drop)="onFileDrop($event)"
             (click)="csvInput.click()">
            <mat-icon class="upload-icon">cloud_upload</mat-icon>
            <span>Drag &amp; drop a file here, or <u>click to browse</u></span>
            <input #csvInput type="file" accept=".csv,.xlsx,.xls" style="display:none"
                   (change)="onFileInputChange($event)">
        </div>

        <p *ngIf="parseError" class="error-text">{{ parseError }}</p>

        <div class="template-hint">
            <button type="button" mat-button color="primary" (click)="downloadTemplate()">
                <mat-icon>download</mat-icon> Download Excel template
            </button>
        </div>

        <mat-divider class="hint-divider"></mat-divider>

        <p class="hint-text">
            <strong>Expected columns:</strong><br>
            <code>PurchaseOrderNumber</code>, <code>RequestNumber</code>,
            <code>SubmitDate</code> (yyyy-MM-dd), <code>Fabricator</code> (select from the dropdown).<br>
            Either <em>PurchaseOrderNumber</em> or <em>RequestNumber</em> is required per row.
            <em>SubmitDate</em> is required when a PO Number is provided.
        </p>
    </ng-container>

    <!-- ── PREVIEWING: table ── -->
    <ng-container *ngIf="state === 'previewing'">
        <div class="preview-header">
            <span class="preview-filename"><mat-icon>insert_drive_file</mat-icon> {{ file?.name }}</span>
            <button type="button" mat-button (click)="reset()">Change file</button>
        </div>

        <p *ngIf="hasRowErrors" class="warn-text">
            <mat-icon class="inline-icon warn-icon">warning</mat-icon>
            {{ rows.length - validRowCount }} row(s) have validation errors and will be skipped.
            Only the {{ validRowCount }} valid row(s) will be submitted.
        </p>

        <div class="table-wrapper">
            <table mat-table [dataSource]="rows" class="preview-table">

                <ng-container matColumnDef="row">
                    <th mat-header-cell *matHeaderCellDef>#</th>
                    <td mat-cell *matCellDef="let row; let i = index"
                        [class.row-error]="row._errors.length > 0">
                        {{ i + 1 }}
                    </td>
                </ng-container>

                <ng-container matColumnDef="PONumber">
                    <th mat-header-cell *matHeaderCellDef>PO Number</th>
                    <td mat-cell *matCellDef="let row"
                        [class.row-error]="row._errors.length > 0">
                        {{ row.PurchaseOrderNumber || '—' }}
                    </td>
                </ng-container>

                <ng-container matColumnDef="RequestNumber">
                    <th mat-header-cell *matHeaderCellDef>Request #</th>
                    <td mat-cell *matCellDef="let row"
                        [class.row-error]="row._errors.length > 0">
                        {{ row.RequestNumber || '—' }}
                    </td>
                </ng-container>

                <ng-container matColumnDef="SubmitDate">
                    <th mat-header-cell *matHeaderCellDef>Submit Date</th>
                    <td mat-cell *matCellDef="let row"
                        [class.row-error]="row._errors.length > 0">
                        {{ row.SubmitDate || '—' }}
                    </td>
                </ng-container>

                <ng-container matColumnDef="Vendor">
                    <th mat-header-cell *matHeaderCellDef>Vendor</th>
                    <td mat-cell *matCellDef="let row"
                        [class.row-error]="row._errors.length > 0">
                        {{ row._vendorName || '—' }}
                    </td>
                </ng-container>

                <ng-container matColumnDef="Errors">
                    <th mat-header-cell *matHeaderCellDef>Issues</th>
                    <td mat-cell *matCellDef="let row"
                        [class.row-error]="row._errors.length > 0">
                        <span *ngFor="let err of row._errors" class="row-error-msg">{{ err }}</span>
                    </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="previewColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: previewColumns;"></tr>
            </table>
        </div>
    </ng-container>

    <!-- ── SUBMITTING: spinner ── -->
    <ng-container *ngIf="state === 'submitting'">
        <div class="spinner-wrap">
            <mat-spinner diameter="48"></mat-spinner>
            <p>Importing purchase orders…</p>
        </div>
    </ng-container>

    <!-- ── DONE: result summary ── -->
    <ng-container *ngIf="state === 'done'">
        <div class="result-summary">
            <mat-icon class="result-icon"
                [class.success-icon]="result.FailureCount === 0"
                [class.warn-icon]="result.FailureCount > 0">
                {{ result.FailureCount === 0 ? 'check_circle' : 'warning' }}
            </mat-icon>
            <div>
                <strong>{{ result.SuccessCount }}</strong> of
                <strong>{{ result.TotalRecords }}</strong> purchase order(s) imported successfully.
                <span *ngIf="result.FailureCount > 0" class="warn-text">
                    {{ result.FailureCount }} failed.
                </span>
            </div>
        </div>

        <div *ngIf="result.Errors && result.Errors.length > 0" class="error-list">
            <p class="error-list-title">Error details:</p>
            <table mat-table [dataSource]="result.Errors" class="error-table">

                <ng-container matColumnDef="row">
                    <th mat-header-cell *matHeaderCellDef>Row</th>
                    <td mat-cell *matCellDef="let e">{{ e.RecordIndex + 1 }}</td>
                </ng-container>

                <ng-container matColumnDef="identifier">
                    <th mat-header-cell *matHeaderCellDef>Identifier</th>
                    <td mat-cell *matCellDef="let e">{{ e.Identifier }}</td>
                </ng-container>

                <ng-container matColumnDef="message">
                    <th mat-header-cell *matHeaderCellDef>Reason</th>
                    <td mat-cell *matCellDef="let e">{{ e.Message }}</td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="errorColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: errorColumns;"></tr>
            </table>
        </div>
    </ng-container>

</mat-dialog-content>

<mat-dialog-actions align="end">

    <ng-container *ngIf="state === 'idle'">
        <button type="button" mat-button (click)="onCancel()">Cancel</button>
    </ng-container>

    <ng-container *ngIf="state === 'previewing'">
        <button type="button" mat-button (click)="onCancel()">Cancel</button>
        <button type="button" mat-raised-button color="primary"
                [disabled]="validRowCount === 0"
                (click)="onSubmit()">
            Import {{ validRowCount }} record(s)
        </button>
    </ng-container>

    <ng-container *ngIf="state === 'submitting'">
        <button type="button" mat-button disabled>Importing…</button>
    </ng-container>

    <ng-container *ngIf="state === 'done'">
        <button type="button" mat-raised-button color="primary" (click)="onCloseSuccess()">
            Close &amp; Refresh
        </button>
    </ng-container>

</mat-dialog-actions>
